import Icon from "./Icon.jsx";
import { useAuth } from "../context/AuthContext.jsx";

export function Results({ results, heartsLeft, onSeeReport }) {
  const correctCount = results.filter((r) => r.correct).length;
  const gemsEarned = results.reduce((s, r) => s + (r.gems || 0), 0);
  const xpEarned = correctCount * 25;

  return (
    <div
      className="fade-up"
      style={{ maxWidth: 640, margin: "60px auto", padding: "0 24px", textAlign: "center" }}
    >
      <div style={{ fontSize: 64, marginBottom: 8 }}>
        {correctCount >= 4 ? "🎉" : correctCount >= 2 ? "✨" : "💪"}
      </div>
      <h1
        className="serif"
        style={{ fontSize: 56, letterSpacing: "-0.02em", margin: "0 0 12px", lineHeight: 1 }}
      >
        {correctCount >= 4
          ? "Checkpoint cleared!"
          : correctCount >= 2
          ? "Solid run."
          : "Keep at it."}
      </h1>
      <p style={{ color: "var(--ink-3)", fontSize: 17, marginBottom: 40 }}>
        {correctCount} of {results.length} correct. You earned <strong>{gemsEarned} gems</strong> and{" "}
        <strong>{xpEarned} XP</strong>.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 32 }}>
        <StatTile icon="gem" big={`+${gemsEarned}`} label="gems earned" tone="#4B7B8A" bg="var(--gem-wash)" />
        <StatTile icon="bolt" big={`+${xpEarned}`} label="XP" tone="#7A5A1F" bg="var(--gold-wash)" />
        <StatTile icon="heart" big={`${heartsLeft}/5`} label="hearts left" tone="#8A2A2A" bg="#F4D9D9" />
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button className="btn btn-terracotta" onClick={onSeeReport}>
          See your report <Icon name="arrow-right" size={14} color="#fff" />
        </button>
      </div>
    </div>
  );
}

function StatTile({ icon, big, label, tone, bg }) {
  return (
    <div className="card" style={{ padding: "22px 10px", background: bg, border: "1px solid " + tone }}>
      <div style={{ marginBottom: 6 }}>
        <Icon name={icon} size={20} />
      </div>
      <div
        className="serif"
        style={{ fontSize: 32, color: tone, letterSpacing: "-0.01em", marginBottom: 2 }}
      >
        {big}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

export function FinalReport({ state, results, onCertificate }) {
  const { user } = useAuth();
  const firstName = (user?.name || "Learner").split(" ")[0];
  const totalCorrect = results.filter((r) => r.correct).length;
  const accuracy = Math.round((totalCorrect / results.length) * 100);
  const score = Math.round(accuracy * 0.8 + Math.min(100, state.gems / 2) * 0.2);

  return (
    <div className="page fade-up" style={{ maxWidth: 900 }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>Your final report</div>
      <h1
        className="serif"
        style={{ fontSize: 60, margin: "0 0 20px", letterSpacing: "-0.02em", lineHeight: 1.02 }}
      >
        Nicely done, <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>{firstName}</span>.
      </h1>
      <p style={{ fontSize: 17, color: "var(--ink-3)", maxWidth: 560, marginBottom: 44 }}>
        Here's how your run through <strong>Prompt Engineering · Foundations</strong> shook out.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16, marginBottom: 32 }}>
        <div
          className="card-raised"
          style={{
            padding: 32,
            background: "var(--ink)",
            color: "var(--cream)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div className="eyebrow" style={{ color: "var(--cream-3)", marginBottom: 12 }}>Overall score</div>
          <div
            className="serif"
            style={{
              fontSize: 120,
              lineHeight: 0.95,
              letterSpacing: "-0.03em",
              color: "var(--terracotta-2)",
            }}
          >
            {score}
          </div>
          <div style={{ color: "var(--cream-3)", marginTop: 8 }}>
            out of 100 · {gradeLetter(score)} grade
          </div>
          <div
            style={{
              position: "absolute",
              right: -30,
              top: -30,
              opacity: 0.08,
              fontSize: 240,
              fontFamily: "var(--font-serif)",
            }}
          >
            ✳
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateRows: "repeat(3, 1fr)", gap: 14 }}>
          <ReportStat label="Accuracy" value={`${accuracy}%`} sub={`${totalCorrect} / ${results.length} correct`} />
          <ReportStat label="Total gems" value={state.gems} sub="currency for hints & lives" icon="gem" />
          <ReportStat label="XP earned" value={state.xp} sub={`Level ${state.level}`} icon="bolt" />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 32 }}>
        <div className="card" style={{ padding: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 10, color: "var(--moss)" }}>Your strengths</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {[
              "Writing audience-specific prompts",
              "Defining output formats (JSON, bullets)",
              "Spotting vague constraints",
            ].map((t, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: i < 2 ? "1px solid var(--line)" : "none",
                }}
              >
                <Icon name="check" size={16} color="var(--moss)" strokeWidth={2.4} />
                <span style={{ fontSize: 14 }}>{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="card" style={{ padding: 24 }}>
          <div className="eyebrow" style={{ marginBottom: 10, color: "var(--terracotta)" }}>Worth revisiting</div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {["Handling missing-info fallbacks explicitly", "Using few-shot examples"].map((t, i) => (
              <li
                key={i}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: i < 1 ? "1px solid var(--line)" : "none",
                }}
              >
                <Icon name="target" size={16} color="var(--terracotta)" />
                <span style={{ fontSize: 14 }}>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card" style={{ padding: 24, marginBottom: 32 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>Question by question</div>
        {results.map((r, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "12px 0",
              borderBottom: i < results.length - 1 ? "1px solid var(--line)" : "none",
            }}
          >
            <div className="mono" style={{ fontSize: 12, color: "var(--ink-3)", width: 30 }}>
              Q{i + 1}
            </div>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: r.correct ? "var(--moss)" : "var(--heart)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
              }}
            >
              {r.correct ? (
                <Icon name="check" size={14} color="#fff" strokeWidth={2.6} />
              ) : (
                <Icon name="x" size={14} color="#fff" strokeWidth={2.6} />
              )}
            </div>
            <div style={{ flex: 1, fontSize: 14, color: "var(--ink-2)" }}>
              {
                [
                  "MCQ · Audience & format",
                  "Fill-in · Clarify a vague prompt",
                  "Code · Add a JSON schema",
                  "Short answer · Missing-info handling",
                  "MCQ · Soft vs. measurable constraints",
                ][i]
              }
            </div>
            <div
              className="mono"
              style={{ fontSize: 12, color: r.correct ? "var(--moss)" : "var(--ink-3)" }}
            >
              +{r.gems} 💎
            </div>
          </div>
        ))}
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          className="btn btn-terracotta"
          style={{ padding: "16px 28px", fontSize: 16 }}
          onClick={onCertificate}
        >
          <Icon name="trophy" size={16} color="#fff" /> Collect your certificate
        </button>
      </div>
    </div>
  );
}

export function gradeLetter(s) {
  return s >= 90 ? "A" : s >= 80 ? "B" : s >= 70 ? "C" : s >= 60 ? "D" : "F";
}

function ReportStat({ label, value, sub, icon }) {
  return (
    <div className="card" style={{ padding: 16, display: "flex", alignItems: "center", gap: 14 }}>
      {icon && (
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "var(--cream-2)",
            display: "grid",
            placeItems: "center",
          }}
        >
          <Icon name={icon} size={18} />
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div className="eyebrow" style={{ marginBottom: 2 }}>{label}</div>
        <div className="serif" style={{ fontSize: 28, letterSpacing: "-0.01em", lineHeight: 1 }}>
          {value}
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{sub}</div>
      </div>
    </div>
  );
}

export function Certificate({ state, results, onBackHome }) {
  const { user } = useAuth();
  const fullName = user?.name || "Learner";
  const totalCorrect = results.filter((r) => r.correct).length;
  const accuracy = Math.round((totalCorrect / results.length) * 100);
  const score = Math.round(accuracy * 0.8 + Math.min(100, state.gems / 2) * 0.2);
  const date = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="page fade-up" style={{ maxWidth: 960, textAlign: "center" }}>
      <div className="eyebrow" style={{ marginBottom: 10 }}>Awarded to the craftsman</div>
      <h1 className="serif" style={{ fontSize: 48, margin: "0 0 32px", letterSpacing: "-0.02em" }}>
        Your certificate
      </h1>

      <div
        style={{
          position: "relative",
          margin: "0 auto 32px",
          padding: 48,
          background: "#FBF7F0",
          border: "1px solid var(--line)",
          borderRadius: 6,
          boxShadow: "var(--shadow-lg)",
          aspectRatio: "1.41 / 1",
          maxWidth: 860,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 18,
            border: "1px solid var(--line-2)",
            borderRadius: 2,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 24,
            border: "3px double var(--line-2)",
            borderRadius: 2,
            pointerEvents: "none",
          }}
        />
        {[0, 1, 2, 3].map((i) => {
          const pos = [
            { top: 40, left: 40 },
            { top: 40, right: 40 },
            { bottom: 40, left: 40 },
            { bottom: 40, right: 40 },
          ][i];
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                ...pos,
                fontFamily: "var(--font-serif)",
                color: "var(--terracotta)",
                fontSize: 26,
              }}
            >
              ✳
            </div>
          );
        })}

        <div style={{ position: "relative", paddingTop: 10 }}>
          <div
            style={{
              width: 70,
              height: 70,
              margin: "0 auto 18px",
              borderRadius: "50%",
              background: "var(--ink)",
              color: "var(--terracotta-2)",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--font-serif)",
              fontSize: 40,
            }}
          >
            L
          </div>

          <div
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.2em",
              color: "var(--ink-3)",
              marginBottom: 14,
            }}
          >
            LEARNCLAUDE · CERTIFICATE OF COMPLETION
          </div>

          <div
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              color: "var(--ink-3)",
              fontSize: 18,
              marginBottom: 6,
            }}
          >
            Presented to
          </div>
          <div
            className="serif"
            style={{ fontSize: 56, letterSpacing: "-0.02em", margin: "0 0 16px", lineHeight: 1 }}
          >
            {fullName}
          </div>

          <div
            style={{
              fontFamily: "var(--font-serif)",
              fontStyle: "italic",
              color: "var(--ink-3)",
              fontSize: 16,
              marginBottom: 6,
            }}
          >
            for completing
          </div>
          <div
            className="serif"
            style={{ fontSize: 32, letterSpacing: "-0.01em", marginBottom: 22 }}
          >
            Prompt Engineering ·{" "}
            <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>Foundations</span>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
              maxWidth: 560,
              margin: "0 auto 28px",
            }}
          >
            <CertStat label="Final score" value={`${score}`} suffix="/100" />
            <CertStat label="Accuracy" value={`${accuracy}%`} />
            <CertStat label="Grade" value={gradeLetter(score)} />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              padding: "0 40px",
              marginTop: 24,
            }}
          >
            <div style={{ textAlign: "left" }}>
              <div
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontSize: 26,
                  borderBottom: "1px solid var(--ink-3)",
                  paddingBottom: 2,
                  width: 160,
                }}
              >
                Claude
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  marginTop: 6,
                  letterSpacing: "0.1em",
                }}
              >
                ISSUING INSTRUCTOR
              </div>
            </div>
            <div
              style={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "var(--terracotta)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontFamily: "var(--font-serif)",
                fontSize: 14,
                textAlign: "center",
                lineHeight: 1.1,
                boxShadow:
                  "inset 0 0 0 3px #fff, inset 0 0 0 4px var(--terracotta), 0 6px 16px -6px rgba(201,100,66,0.6)",
              }}
            >
              <div>
                <div style={{ fontSize: 20 }}>✳</div>
                <div style={{ fontSize: 9, letterSpacing: "0.1em" }}>VERIFIED</div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div
                style={{
                  fontFamily: "var(--font-serif)",
                  fontStyle: "italic",
                  fontSize: 22,
                  borderBottom: "1px solid var(--ink-3)",
                  paddingBottom: 2,
                  width: 160,
                  marginLeft: "auto",
                }}
              >
                {date}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: "var(--ink-3)",
                  marginTop: 6,
                  letterSpacing: "0.1em",
                }}
              >
                DATE AWARDED
              </div>
            </div>
          </div>

          <div
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--ink-4)",
              marginTop: 16,
              letterSpacing: "0.1em",
            }}
          >
            Cert ID · LC-{Math.random().toString(36).slice(2, 8).toUpperCase()} · verify at learnclaude.app/c
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
        <button className="btn btn-ghost" onClick={() => window.print()}>
          Print / save PDF
        </button>
        <button className="btn btn-primary" onClick={onBackHome}>
          Back to tracks <Icon name="arrow-right" size={14} color="var(--cream)" />
        </button>
      </div>
    </div>
  );
}

function CertStat({ label, value, suffix }) {
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: 10,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div className="serif" style={{ fontSize: 38, letterSpacing: "-0.02em", lineHeight: 1 }}>
        {value}
        <span style={{ fontSize: 18, color: "var(--ink-3)" }}>{suffix}</span>
      </div>
    </div>
  );
}
